import React, { useReducer, useEffect } from 'react';
import { useRequestData, useLabels } from 'wsm-common-data';
import PropTypes from 'prop-types';
import { formatNumber } from 'ddc-intl';
import { hasUrlParam } from 'wsm-srp-utilities';
import Slider from 'rc-slider';

import { trackEvent } from 'ddc-track-event';

import {
	rangeContainsInventory,
	getLowerBound,
	getUpperBound,
	fixRangeForSubmit,
	getBucketMap,
	isNoPriceActive,
	roundToNearestBucket,
	convertCameltoSnakeCase
} from '../../utilities/facet-user-range';
import RangeForm from './RangeForm';
import NoPriceButton from './NoPriceButton';
import { noPrice } from '../../global-constants';

const rangeReducer = (state, action) => {
	switch (action.type) {
		case 'CHANGE_SLIDER_MIN':
			return {
				...state,
				// rc-slider adds to the min value to calculate max in a range slider
				sliderMin: action.payload.min === 1 ? 0 : action.payload.min,
				formInputMin: action.payload.min
			};
		case 'CHANGE_SLIDER_MAX':
			return {
				...state,
				sliderMax: action.payload.sliderMax,
				formInputMax: action.payload.max
			};
		case 'CHANGE_FORM_INPUT_MIN':
			return {
				...state,
				...(typeof action.payload.sliderMin === 'number' && {
					sliderMin:
						action.payload.sliderMin === 1
							? 0
							: action.payload.sliderMin
				}),
				formInputMin: action.payload.min
			};
		case 'CHANGE_FORM_INPUT_MAX':
			return {
				...state,
				...(typeof action.payload.sliderMax === 'number' && {
					sliderMax: action.payload.sliderMax
				}),
				formInputMax: action.payload.max
			};
		default:
			return {
				...state
			};
	}
};

const FacetUserRange = ({
	facetValues,
	selectedValues,
	facetId,
	noPriceObj,
	isMultiselect,
	facetTitle
}) => {
	const {
		min,
		max,
		bucketSize,
		values,
		selectedMin = null,
		selectedMax = null,
		isCurrency = false
	} = facetValues;
	const { widgetName, windowId, locale } = useRequestData();
	const labels = useLabels();

	// Setting a variable to offset the range slider when bucketSize equals 1.
	// This `offset` variable is incorporated within the max/min state values
	// to allow the slider to display properly for "year" facets.
	// This enables a range of a single year to be set, ie 2023-2023
	const offset = bucketSize === 1 ? 1 : 0;
	// Lowest and highest buckets
	const bottomLimit = getLowerBound(min, bucketSize, facetId, isMultiselect);
	const topLimit = getUpperBound(max + offset, bucketSize);

	// Values of min and max forms, slider
	const currentMin =
		selectedMin !== null ? Math.max(selectedMin, bottomLimit) : bottomLimit;
	const currentMax =
		selectedMax !== null
			? Math.min(selectedMax, topLimit)
			: topLimit - offset;

	const [{ sliderMin, formInputMin, sliderMax, formInputMax }, dispatch] =
		useReducer(rangeReducer, {
			sliderMin: currentMin,
			formInputMin: currentMin,
			sliderMax: currentMax + offset,
			formInputMax: currentMax
		});

	// this useEffect is needed to update user ranges when filters are cleared / changed
	useEffect(() => {
		if (currentMin !== formInputMin) {
			dispatch({
				type: 'CHANGE_FORM_INPUT_MIN',
				payload: {
					min: currentMin,
					sliderMin: getLowerBound(
						currentMin,
						bucketSize,
						facetId,
						isMultiselect
					)
				}
			});
		}
		if (currentMax !== formInputMax) {
			dispatch({
				type: 'CHANGE_FORM_INPUT_MAX',
				payload: {
					max: currentMax + offset,
					sliderMax: getUpperBound(currentMax + offset, bucketSize)
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMin, currentMax]);

	const isDisabled = !hasUrlParam(window.location.href, facetId);

	// Map of buckets containing values with bucket values as keys
	const bucketMap = getBucketMap(values);

	const handleSubmit = (newMin, newMax) => {
		const isMinChange = newMin !== currentMin;
		const isNewRange = newMin !== currentMin || newMax !== currentMax;
		const [bottom, top] = fixRangeForSubmit(
			newMin,
			newMax,
			bottomLimit,
			topLimit,
			bucketSize,
			isMinChange
		);
		const containsInventory = rangeContainsInventory(
			bottom,
			top,
			bucketSize,
			bucketMap,
			facetId,
			isMultiselect
		);
		if (isNewRange && containsInventory) {
			if (isMinChange) {
				dispatch({
					type: 'CHANGE_FORM_INPUT_MIN',
					payload: {
						min: bottom,
						sliderMin: getLowerBound(
							bottom,
							bucketSize,
							facetId,
							isMultiselect
						)
					}
				});
			} else {
				dispatch({
					type: 'CHANGE_FORM_INPUT_MAX',
					payload: {
						max: top,
						sliderMax: getUpperBound(top + offset, bucketSize)
					}
				});
			}
			const updatedSelections = [];
			const sliderValue = `${bottom}-${top}`;

			updatedSelections.push(sliderValue);

			if (
				selectedValues &&
				selectedValues.find((value) => value.value === noPrice)
			) {
				updatedSelections.push(noPrice);
			}

			window.DDC.InvData.setFilterSelections({
				filterName: facetId,
				filterValue: updatedSelections[0],
				filterValues: updatedSelections,
				filterTitleText: facetTitle,
				isChip: false,
				isSelected: false,
				isSlider: true,
				element: document.getElementById(facetId)
			});
		}
		// case where new range isn't new  or doesn't contain inventory: change state back to current
		else {
			dispatch({
				type: 'CHANGE_FORM_INPUT_MIN',
				payload: { min: currentMin }
			});
			dispatch({
				type: 'CHANGE_FORM_INPUT_MAX',
				payload: { max: currentMax }
			});
		}
	};

	const handleSliderSubmit = (newMin, newMax) => {
		handleSubmit(newMin, newMax - offset);
	};

	const updateSliderValues = (minVal, maxVal) => {
		const isMinChange = minVal !== formInputMin;
		if (isMinChange) {
			const bottomBucket = getLowerBound(
				minVal,
				bucketSize,
				facetId,
				isMultiselect
			);

			dispatch({
				type: 'CHANGE_SLIDER_MIN',
				payload: { min: bottomBucket }
			});
		} else {
			const topBucket = getUpperBound(maxVal, bucketSize);

			dispatch({
				type: 'CHANGE_SLIDER_MAX',
				payload: { sliderMax: topBucket, max: topBucket - offset }
			});
		}
	};

	const getVehicleCount = () => {
		const vehicleCount = window.DDC.InvData.getFacets().totalCount;
		const matchesLabel =
			vehicleCount !== 1 ? labels.get('MATCHES') : labels.get('MATCH');
		return `${formatNumber(vehicleCount, locale)} ${matchesLabel}`;
	};

	const getAriaLabel = () => {
		return [
			labels.get(`MIN_${convertCameltoSnakeCase(facetId)}`.toUpperCase()),
			labels.get(`MAX_${convertCameltoSnakeCase(facetId)}`.toUpperCase())
		];
	};

	return (
		<div className="facet-list-range-slider">
			<RangeForm
				id={facetId}
				minValue={bottomLimit}
				maxValue={topLimit}
				minInput={formInputMin}
				maxInput={formInputMax}
				minLabel={labels.get('MIN')}
				maxLabel={labels.get('MAX')}
				handleSubmit={handleSubmit}
				isCurrency={isCurrency}
				facetTitle={facetTitle}
			/>
			<div className="vehicle-count">{getVehicleCount()}</div>
			<div className="range-slider">
				<Slider
					range
					allowCross={false} // don't allow sliders to cross
					pushable={bucketSize} // minimum ensured distance between handles
					defaultValue={[sliderMin, sliderMax]}
					value={sliderMin !== null ? [sliderMin, sliderMax] : null} // rc-slider focuses the handles if these are set initially
					min={bottomLimit}
					max={topLimit}
					dots
					step={bucketSize}
					ariaLabelForHandle={getAriaLabel()}
					onChange={(e) => {
						updateSliderValues(
							getLowerBound(
								e[0],
								bucketSize,
								facetId,
								isMultiselect
							),
							roundToNearestBucket(e[1], bucketSize)
						);
					}}
					onAfterChange={(e) => {
						handleSliderSubmit(
							getLowerBound(
								e[0],
								bucketSize,
								facetId,
								isMultiselect
							),
							roundToNearestBucket(e[1], bucketSize)
						);
						trackEvent(widgetName, windowId, {
							action: 'applied',
							element: `${facetId} selection`,
							elementCTA: 'Range Slider',
							result: 'Results updated with facet added',
							dataToExcludeFromWidgetUserEvent: {
								fieldValue: `${e[0]}-${e[1]}`,
								facetId,
								facetTitle
							}
						});
					}}
				/>
			</div>
			{noPriceObj && noPriceObj.count && (
				<NoPriceButton
					id={facetId}
					noPriceCount={noPriceObj.count}
					noPriceLabel={noPriceObj.label}
					selectedValues={selectedValues}
					isSelected={isNoPriceActive(selectedValues)}
					isDisabled={isDisabled}
					facetTitle={facetTitle}
				/>
			)}
		</div>
	);
};

FacetUserRange.propTypes = {
	facetValues: PropTypes.shape({
		min: PropTypes.number.isRequired,
		max: PropTypes.number.isRequired,
		bucketSize: PropTypes.number.isRequired,
		values: PropTypes.arrayOf(
			PropTypes.shape({
				count: PropTypes.number.isRequired,
				label: PropTypes.string.isRequired,
				type: PropTypes.string.isRequired,
				value: PropTypes.string.isRequired
			})
		).isRequired,
		selectedMin: PropTypes.number,
		selectedMax: PropTypes.number,
		isCurrency: PropTypes.bool
	}).isRequired,
	noPriceObj: PropTypes.shape({
		count: PropTypes.number,
		label: PropTypes.string
	}),
	facetId: PropTypes.string.isRequired,
	isMultiselect: PropTypes.bool.isRequired,
	selectedValues: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
	facetTitle: PropTypes.string
};

FacetUserRange.defaultProps = {
	noPriceObj: null
};

export default FacetUserRange;
